import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import useNetworkStatus from '../../../hooks/useNetworkStatus';
import { setConnectionStatus, showToast } from '../../../store/notificationSlice';
import {
  markOfflineNoticeShown,
  resetOnConnectionRecovered,
  shouldShowOfflineNotice,
} from '../../../util/networkNoticeState';
import classes from './ConnectionBanner.module.css';

const OFFLINE_TOAST = {
  type: 'network',
  title: 'Keine Verbindung',
  message: 'Keine Internetverbindung. Einige Funktionen sind eingeschränkt.',
  duration: 5000,
};

const ONLINE_TOAST = {
  type: 'success',
  title: 'Wieder online',
  message: 'Deine Verbindung wurde wiederhergestellt.',
  duration: 3500,
};

export const ConnectionBanner = () => {
  const dispatch = useDispatch();
  const showOfflineBanner = useSelector(
    (state) => state.notifications?.connection?.showOfflineBanner
  );
  const { isOnline } = useNetworkStatus();
  const previousOnlineRef = useRef(isOnline);
  const initializedRef = useRef(false);

  useEffect(() => {
    const wasOnline = previousOnlineRef.current;

    dispatch(setConnectionStatus({ isOnline }));

    if (!initializedRef.current) {
      initializedRef.current = true;
      if (!isOnline && shouldShowOfflineNotice()) {
        dispatch(showToast(OFFLINE_TOAST));
        markOfflineNoticeShown();
      }
      previousOnlineRef.current = isOnline;
      return;
    }

    if (wasOnline && !isOnline && shouldShowOfflineNotice()) {
      dispatch(showToast(OFFLINE_TOAST));
      markOfflineNoticeShown();
    }

    if (!wasOnline && isOnline) {
      dispatch(showToast(ONLINE_TOAST));
      resetOnConnectionRecovered();
    }

    previousOnlineRef.current = isOnline;
  }, [dispatch, isOnline]);

  if (!showOfflineBanner) return null;

  return createPortal(
    <div className={classes.banner} role="status" aria-live="polite">
      <span className={classes.icon} aria-hidden>
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M12 8V13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="12" cy="16.5" r="1" fill="currentColor" />
          <path d="M10.3 4.7C11.2 4.25 12 4 12 4C12 4 16.5 5.8 19.5 9.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M6.5 7.5C8.1 6.1 10 5 12 4.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M3.5 12C4.4 10.4 5.8 9 7.5 7.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </span>
      <span className={classes.text}>
        Keine Internetverbindung. Einige Funktionen sind eingeschränkt.
      </span>
    </div>,
    document.body
  );
};

export default ConnectionBanner;
